import {
  ApiError,
  draftFromMessage,
  type ArchiveEventItem,
  type DeliveryStats,
  type Event,
  type Message,
  type MessagePhase,
} from "@/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { fillPlaceholders, formatDateTime } from "@/lib/format";
import {
  BOUNCE_CATEGORY_ORDER,
  EVENT_TYPE_LABEL,
  MESSAGE_PHASE_LABEL,
  bounceCategoryLabel,
  legacySubject,
  typeTone,
} from "@/lib/labels";
import { cn } from "@/lib/cn";
import { Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

type Tone = "default" | "accent" | "warn" | "danger" | "success" | "muted";

const PHASE_TONE: Record<MessagePhase, Tone> = {
  start: "danger",
  update: "warn",
  reminder: "warn",
  close: "success",
  announcement: "accent",
  adhoc: "muted",
};

function phaseLabel(phase: MessagePhase, eventType: string): string {
  if (phase === "close") return eventType === "incident" ? "Behoben" : "Abschluss";
  return MESSAGE_PHASE_LABEL[phase] ?? phase;
}

function isLegacy(message: Message): boolean {
  return message.origin === "legacy_import";
}

function subjectOf(message: Message, event: Event): string {
  const subject = isLegacy(message)
    ? legacySubject(message.subject_de)
    : fillPlaceholders(message.subject_de, event.placeholders ?? {});
  return subject || "Ohne Betreff";
}

function berlinDay(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("de-DE", {
        timeZone: "Europe/Berlin",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
}

function formatRange(first: string, last: string): string {
  const a = berlinDay(first);
  const b = berlinDay(last);
  if (a === b) return a;
  const [da, ma, ya] = a.split(".");
  const [db, mb, yb] = b.split(".");
  if (ma === mb && ya === yb) return `${da}.–${db}.${mb}.`;
  if (ya === yb) return `${da}.${ma}.–${db}.${mb}.`;
  return `${a}–${b}`;
}

function sentAt(message: Message): string {
  return message.updated_at;
}

// Bounces mostly arrive within hours; before that "accepted" is all we know.
const BOUNCE_SETTLE_MS = 24 * 60 * 60 * 1000;

function count(n: number): string {
  return n.toLocaleString("de-DE");
}

function bouncesLink(eventId: string, messageId?: string): string {
  const params = new URLSearchParams({ tab: "reports", kind: "failed", event: eventId });
  if (messageId) params.set("message", messageId);
  return `/bounces?${params.toString()}`;
}

function Dot({ className }: { className: string }) {
  return <span aria-hidden="true" className={cn("mr-1 inline-block h-1.5 w-1.5 rounded-full", className)} />;
}

function DeliveryStatsLine({
  stats,
  sentAt,
  eventId,
  messageId,
  compact = false,
}: {
  stats: DeliveryStats | null | undefined;
  sentAt: string;
  eventId: string;
  messageId?: string;
  compact?: boolean;
}) {
  if (!stats || stats.total <= 0) return null;
  const settling = Date.now() - new Date(sentAt).getTime() < BOUNCE_SETTLE_MS;
  const categories = [
    ...BOUNCE_CATEGORY_ORDER.filter((key) => (stats.bounce_categories[key] ?? 0) > 0),
    ...Object.keys(stats.bounce_categories).filter(
      (key) => !(BOUNCE_CATEGORY_ORDER as readonly string[]).includes(key),
    ),
  ];
  const problems = stats.bounced + stats.rejected + stats.failed + stats.unknown;
  return (
    <span className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-[11px] tabular-nums">
      {settling ? (
        <span className="text-muted" title="SMTP-Annahme; Rückläufer können noch kommen">
          <Dot className="bg-muted" />
          {count(stats.submitted)} angenommen
        </span>
      ) : (
        <span title="SMTP-Annahme ohne Rückläufer">
          <Dot className="bg-green" />
          {count(stats.delivered)} zugestellt
        </span>
      )}
      {stats.bounced > 0 ? (
        <span className="group relative">
          <Link
            to={bouncesLink(eventId, messageId)}
            className="text-amber underline decoration-dotted underline-offset-2 hover:decoration-solid"
          >
            <Dot className="bg-amber" />
            {count(stats.bounced)} Rückläufer
          </Link>
          <span
            role="tooltip"
            className={cn(
              "pointer-events-none absolute top-full z-20 mt-1 hidden w-56 rounded-md bg-ink px-2.5 py-2 text-[11px] leading-relaxed text-bg shadow-lg group-focus-within:block group-hover:block",
              messageId ? "right-0" : "left-0",
            )}
          >
            <span className="block font-semibold">Rückläufer nach Ursache</span>
            {categories.map((key) => (
              <span key={key} className="flex justify-between gap-3">
                <span>{bounceCategoryLabel(key)}</span>
                <span>{count(stats.bounce_categories[key] ?? 0)}</span>
              </span>
            ))}
            <span className="mt-1 block opacity-70">Klicken für die Liste</span>
          </span>
        </span>
      ) : null}
      {stats.rejected > 0 ? (
        <span className="text-danger">
          <Dot className="bg-danger" />
          {count(stats.rejected)} abgelehnt
        </span>
      ) : null}
      {stats.failed > 0 ? (
        <span className="text-danger">
          <Dot className="bg-danger" />
          {count(stats.failed)} fehlgeschlagen
        </span>
      ) : null}
      {stats.unknown > 0 ? (
        <span className="text-amber">
          <Dot className="bg-amber" />
          {count(stats.unknown)} unbekannt
        </span>
      ) : null}
      {stats.queued > 0 ? (
        <span className="text-accent">
          <Dot className="bg-accent" />
          {count(stats.queued)} in der Queue
        </span>
      ) : null}
      {compact ? null : settling ? (
        <span className="italic text-muted">Rückläufer laufen noch ein</span>
      ) : problems === 0 ? (
        <span className="text-muted">keine Rückläufer</span>
      ) : null}
    </span>
  );
}

function PhasePill({ phase, eventType }: { phase: MessagePhase; eventType: string }) {
  return <Badge tone={PHASE_TONE[phase] ?? "muted"}>{phaseLabel(phase, eventType)}</Badge>;
}

function VorgangRow({ item, startOpen }: { item: ArchiveEventItem; startOpen: boolean }) {
  const [open, setOpen] = useState(startOpen);
  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { event, messages } = item;
  const first = messages[0];
  const allLegacy = messages.length > 0 && messages.every(isLegacy);
  const title = event.title || (first ? subjectOf(first, event) : "Ohne Titel");

  const parts: string[] = [];
  if (messages.length === 1) parts.push("1 Mail");
  else parts.push(`${messages.length} Mails · ${formatRange(item.first_sent_at, item.last_sent_at)}`);
  if (item.recipient_count > 0) parts.push(`${item.recipient_count} Empfänger`);
  else if (allLegacy) parts.push("Mailman-Archiv");

  // The opening mail (announcement / start) is the one worth reusing for a new event.
  async function openAsDraft() {
    if (!first) return;
    setDrafting(true);
    setDraftError(null);
    try {
      const draft = await draftFromMessage(first.id);
      navigate(`/events/${draft.event_id}?message=${draft.id}`);
    } catch (err) {
      setDraftError(err instanceof ApiError ? err.message : "Entwurf konnte nicht angelegt werden.");
      setDrafting(false);
    }
  }

  return (
    <li className="[&:first-child>div]:rounded-t-lg [&:last-child>div]:rounded-b-lg">
      <div className="flex items-start gap-2 px-3 py-2 hover:bg-surface-subtle">
        {messages.length > 1 ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-label={open ? "Mails ausblenden" : "Mails anzeigen"}
            className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted hover:bg-surface-subtle hover:text-ink focus:outline-none focus:ring-1 focus:ring-accent"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 16 16"
              className={cn("h-4 w-4 transition-transform", open && "rotate-90")}
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 3.5 10.5 8 6 12.5" />
            </svg>
          </button>
        ) : (
          <span className="mt-0.5 h-6 w-6 shrink-0" aria-hidden="true" />
        )}
        <div className="min-w-0 flex-1">
          <Link to={`/events/${event.id}`} className="block truncate text-sm font-medium hover:underline">
            {title}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-1">
            {messages.map((m, i) => (
              <Fragment key={m.id}>
                {i > 0 ? (
                  <span aria-hidden="true" className="text-[11px] text-muted">
                    →
                  </span>
                ) : null}
                <PhasePill phase={m.phase} eventType={event.type} />
              </Fragment>
            ))}
            <span className="ml-1 text-[11px] text-muted">{parts.join(" · ")}</span>
            {draftError ? <span className="ml-1 text-[11px] text-danger">{draftError}</span> : null}
          </div>
          <div className="mt-1">
            <DeliveryStatsLine stats={item.delivery_stats} sentAt={item.last_sent_at} eventId={event.id} />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {first ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={drafting}
              title={
                messages.length > 1
                  ? "Erste Mail dieses Vorgangs als neuen Entwurf öffnen; Datum und Uhrzeiten bleiben leer"
                  : "Als neuen Entwurf öffnen; Datum und Uhrzeiten bleiben leer"
              }
              onClick={() => void openAsDraft()}
            >
              Als Entwurf
            </Button>
          ) : null}
          <Badge tone={typeTone(event.type)}>{EVENT_TYPE_LABEL[event.type]}</Badge>
          {allLegacy ? <Badge>Archiv</Badge> : null}
          <span className="hidden text-[11px] text-muted tabular-nums sm:block">
            {formatDateTime(item.last_sent_at)}
          </span>
        </div>
      </div>
      {open ? (
        <ul className="ml-8 mb-2 mr-3 divide-y divide-hairline rounded-md border border-hairline bg-surface-subtle">
          {[...messages].reverse().map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 py-1.5">
              <PhasePill phase={m.phase} eventType={event.type} />
              <Link
                to={`/events/${event.id}?message=${m.id}`}
                className="min-w-0 flex-1 truncate text-sm hover:underline"
              >
                {subjectOf(m, event)}
              </Link>
              <DeliveryStatsLine
                stats={m.delivery_stats}
                sentAt={sentAt(m)}
                eventId={event.id}
                messageId={m.id}
                compact
              />
              <span className="text-[11px] text-muted tabular-nums">{formatDateTime(sentAt(m))}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function VorgangList({
  items,
  expandFirstIfMulti = false,
}: {
  items: ArchiveEventItem[];
  expandFirstIfMulti?: boolean;
}) {
  return (
    <ul className="divide-y divide-hairline rounded-lg border border-hairline bg-surface">
      {items.map((item, i) => (
        <VorgangRow
          key={item.event.id}
          item={item}
          startOpen={expandFirstIfMulti && i === 0 && item.messages.length > 1}
        />
      ))}
    </ul>
  );
}
